import { useCallback, useEffect, useState } from 'react';

import { getUsageAnalyticsEvents } from '../api/getUsageAnalyticsEvents';
import { AnalyticsEvent } from '../types/AnalyticsEvent';

type UseAnalyticsEventsProps = {
  societyId?: string;
  feature: string;
  fromDate?: string;
  toDate?: string;
  partnerId?: string;
  page?: number;
  limit?: number;
  partnerSearch?: string;
};

type UseAnalyticsEventsResult = {
  events: AnalyticsEvent[];
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
};

export const useAnalyticsEvents = ({
  societyId,
  feature,
  fromDate,
  toDate,
  partnerId,
  page = 1,
  limit = 10,
  partnerSearch,
}: UseAnalyticsEventsProps): UseAnalyticsEventsResult => {
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    if (!societyId || !feature) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await getUsageAnalyticsEvents({
        societyId,
        feature,
        fromDate,
        toDate,
        partnerId,
        limit,
        page,
        partnerSearch,
      });
      setEvents(response?.events || []);
      setTotal(response?.total || 0);
    } catch (err: any) {
      setError(err?.message || 'Unknown error');
      setEvents([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [societyId, feature, fromDate, toDate, partnerId, page, limit, partnerSearch]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return { events, total, isLoading, error, refetch: fetchEvents };
};

export default useAnalyticsEvents;
